import { traceEvent } from '@wk/wk-gatherer';
import { Button } from '@wk/wk-uni-lib';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useContext } from 'react';
import { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import LeadButton from '@/components/LeadButton';
import TPLink from '@/components/TPLink';
import useTrustpilotScore from '@/components/TrustpilotBar/useTrustpilotScore';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';

import 'swiper/css';

// 奖项数据 - 第一行（主要奖项）
const AWARDS_ROW_1 = [
  {
    id: 'ca',
    name: 'Communicator Award',
    year: '2025',
    highlighted: false,
    logo: '/images/wukong-reviews/logo-1.png',
    widthClass: 'w-[136px] lg:w-[160px] xl:w-[192px]',
  },
  {
    id: 'gsv',
    name: 'GSV 150',
    year: '2025',
    highlighted: false,
    logo: '/images/wukong-reviews/logo-2.png',
    widthClass: 'w-[120px] lg:w-[144px] xl:w-[172px]',
  },
  {
    id: 'ppa',
    name: "Parents' Picks Awards",
    year: '2025',
    highlighted: false,
    logo: '/images/wukong-reviews/logo-3.png',
    widthClass: 'w-[136px] lg:w-[160px] xl:w-[186px]',
  },
  {
    id: 'cognia',
    name: 'Cognia School Of Distinction',
    year: '2024',
    highlighted: true,
    logo: '/images/wukong-reviews/logo-4.png',
    widthClass: 'w-[136px] lg:w-[176px] xl:w-[220px]',
  },
  {
    id: 'shaliwen',
    name: 'No.1 Online K-12 Chinese Program',
    year: '2026',
    highlighted: false,
    logo: '/images/wukong-reviews/logo-14.png',
    widthClass: 'w-[244px] lg:w-[214px] xl:w-[250px]',
  },
  {
    id: 'creative',
    name: 'Creative Child Awards',
    year: '2025',
    highlighted: false,
    logo: '/images/wukong-reviews/logo-5.png',
    widthClass: 'w-[120px] lg:w-[140px] xl:w-[168px]',
  },
  {
    id: 'holen',
    name: 'Holen IQ',
    year: '2022~2025',
    highlighted: false,
    logo: '/images/wukong-reviews/logo-6.png',
    widthClass: 'w-[120px] lg:w-[120px] xl:w-[144px]',
  },
];

// 奖项数据 - 第二行（次要奖项）
const AWARDS_ROW_2 = [
  {
    id: 'gicc',
    name: 'GICC Best Employer',
    year: '2024',
    highlighted: false,
    logo: '/images/wukong-reviews/logo-7.png',
    widthClass: 'w-[120px] lg:w-[148px] xl:w-[174px]',
  },
  {
    id: 'harvard',
    name: 'Harvard China Education Forum',
    subtitle: '2023 Strategic Supporter',
    year: '',
    logo: '/images/wukong-reviews/logo-8.png',
    widthClass: 'w-[146px] lg:w-[202px] xl:w-[238px]',
  },
  {
    id: 'nz',
    name: 'New Zealand Prestige',
    year: '2023',
    logo: '/images/wukong-reviews/logo-9.png',
    widthClass: 'w-[120px] lg:w-[148px] xl:w-[186px]',
  },
  {
    id: 'edtech',
    name: 'EdTech Breakthrough',
    year: '2023',
    logo: '/images/wukong-reviews/logo-10.png',
    widthClass: 'w-[120px] lg:w-[148px] xl:w-[174px]',
  },
  {
    id: 'edtechx',
    name: 'EdTechX Awards',
    year: '2023',
    logo: '/images/wukong-reviews/logo-11.png',
    widthClass: 'w-[120px] lg:w-[148px] xl:w-[172px]',
  },
  {
    id: 'digital',
    name: 'Digital Education Awards',
    year: '2022',
    logo: '/images/wukong-reviews/logo-12.png',
    widthClass: 'w-[130px] lg:w-[166px] xl:w-[196px]',
  },
  {
    id: 'equal',
    name: 'Equal Ocean',
    year: '2022',
    logo: '/images/wukong-reviews/logo-13.png',
    widthClass: 'w-[120px] lg:w-[148px] xl:w-[172px]',
  },
];

/**
 * 奖项卡片组件 - PC端
 */
const AwardCard: React.FC<{
  name: string;
  year: string;
  subtitle?: string;
  highlighted?: boolean;
  widthClass: string;
  logo: string;
  row?: 1 | 2;
}> = ({
  name,
  year,
  subtitle,
  highlighted = false,
  widthClass,
  logo,
  row = 1,
}) => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  // 第一行: lg 32px, xl 36px | 第二行: lg 32px, xl 40px
  const logoHeightClass =
    row === 1 ? 'h-8 lg:h-8 xl:h-9' : 'h-8 lg:h-8 xl:h-10';

  return (
    <div
      className={`relative flex shrink-0 flex-col items-center space-y-3 rounded-lg bg-clip-padding px-2 pb-2 pt-3 xl:px-3 ${widthClass} ${
        highlighted
          ? 'border-[1.5px] border-[#EDCE97]'
          : 'border border-[rgba(217,200,173,0.4)] bg-white'
      }`}
      style={
        highlighted
          ? {
              background:
                'linear-gradient(0deg, rgba(9, 49, 28, 0.87) 0%, rgba(9, 49, 28, 0.87) 100%), linear-gradient(91deg, #FFF8EF -6.82%, #FFF3E2 -2.4%, #FFFBF5 26.52%, #FFF 50.78%, rgba(253, 241, 224, 0.41) 75.66%, #FBEED9 98.79%, #FFFAF3 105.35%), #FFF',
              backgroundBlendMode: 'overlay, normal, normal',
            }
          : undefined
      }>
      {/* Logo 区域 */}
      <div
        className={`relative flex w-full items-center justify-center ${logoHeightClass}`}>
        <Image
          src={logo}
          alt={name}
          width={120}
          height={40}
          quality={100}
          unoptimized
          className="h-full w-auto object-contain"
        />
      </div>

      {/* 文字区域 */}
      <div className="relative flex w-full flex-1 flex-col items-center justify-center space-y-1 text-center">
        <p
          className={`w-full whitespace-nowrap text-[14px] font-semibold leading-[1.2] text-text-primary lg:text-[12px] xl:text-[14px] ${isZh ? '-tracking-wide' : ''}`}>
          {name}
        </p>
        <p className="w-full whitespace-nowrap text-[11px] font-normal text-text-secondary_3 lg:text-[11px] xl:text-[12px]">
          {subtitle || (year ? `- ${year} -` : '')}
        </p>
      </div>
    </div>
  );
};

/**
 * 移动端大卡片组件 - Cognia 卡片
 */
const MobileMainCard: React.FC<{
  name: string;
  year: string;
  subtitle?: string;
  highlighted?: boolean;
  logo: string;
}> = ({ name, year, subtitle, highlighted = false, logo }) => {
  return (
    <div
      className={`relative flex w-full flex-col items-center space-y-3 rounded-xl bg-clip-padding px-8 py-6 ${
        highlighted
          ? 'border-2 border-[#EDCE97]'
          : 'border border-[rgba(217,200,173,0.4)] bg-white'
      }`}
      style={
        highlighted
          ? {
              // 预计算 overlay blend 结果，避免依赖 background-blend-mode（移动端兼容性差）
              background:
                'linear-gradient(91deg, #FFF5E4 -6.82%, #FFEED2 -2.4%, #FFF8ED 26.52%, #FFFDF7 50.78%, #FFEFD6 75.66%, #FFE8C2 98.79%, #FFF5EA 105.35%)',
            }
          : undefined
      }>
      {/* Logo 区域 */}
      <div className="relative flex h-[58px] w-full items-center justify-center">
        <Image
          src={logo}
          alt={name}
          width={284}
          height={58}
          quality={100}
          unoptimized
          className="h-full w-auto object-contain"
        />
      </div>

      {/* 文字区域 */}
      <div className="relative flex w-full flex-col items-center justify-center space-y-2 text-center">
        <p className="w-full text-[18px] font-semibold leading-[1.2] text-text-primary">
          {name}
        </p>
        <p className="w-full text-[16px] font-medium leading-[1.2] text-text-secondary_3">
          {subtitle || (year ? `- ${year} -` : '')}
        </p>
      </div>
    </div>
  );
};

/**
 * 移动端滚动小卡片组件 - 只有 logo 和文字
 */
const MobileScrollCard: React.FC<{
  name: string;
  year: string;
  subtitle?: string;
  logo: string;
}> = ({ name, year, subtitle, logo }) => {
  return (
    <div className="flex w-max min-w-[120px] shrink-0 flex-col items-center space-y-2 rounded-lg border-[1px] border-[rgba(217,200,173,0.4)] bg-white bg-clip-padding px-2 pb-2 pt-3">
      {/* Logo 区域 */}
      <div className="flex h-9 w-full items-center justify-center">
        <Image
          src={logo}
          alt={name}
          width={120}
          height={40}
          quality={100}
          unoptimized
          className="h-full w-auto max-w-full object-contain"
        />
      </div>
      {/* 文字区域 - 文案一行展示完整 */}
      <div className="flex w-fit max-w-full flex-1 flex-col items-center justify-center space-y-1 text-center">
        <p className="whitespace-nowrap text-[14px] font-semibold leading-[1.2] text-text-primary">
          {name}
        </p>
        <p className="w-full text-[12px] font-normal leading-[1.2] text-text-secondary_3">
          {subtitle || (year ? `- ${year} -` : '')}
        </p>
      </div>
    </div>
  );
};

/**
 * Wall of Love 页面顶部主视觉区域
 * 包含：标题、副标题、Trustpilot 评分、奖项卡片展示、CTA 按钮
 */
const HeroSection: React.FC = () => {
  const { t, i18n } = useTranslation('wukong-reviews');
  const { isLogin } = useContext(GlobalLoginContext);
  const isZh = i18n.language === 'zh';
  const rating = useTrustpilotScore();

  // 约课按钮埋点
  const handleBookClassTrace = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('[埋点] C_WallofLove_BookClass', {
      dsSource: '用户之声-头部版块',
      loginStatus: isLogin ? '已登' : '未登',
    });
    traceEvent({
      data: {
        _event: 'C_WallofLove_BookClass',
        dsSource: '用户之声-头部版块',
        loginStatus: isLogin ? '已登' : '未登',
      },
    });
  }, [isLogin]);

  return (
    <section
      className="flex w-full flex-col items-center space-y-6 px-4 py-[50px] pt-[113px] leading-[1.2] lg:space-y-[60px] lg:px-0 lg:pb-[70px] lg:pt-[181px] xl:pb-[90px]"
      style={{
        background:
          'linear-gradient(180deg, #FAF7F0 40%, rgba(250, 247, 240, 0.00) 80.47%, rgba(250, 247, 240, 0.00) 100%)',
      }}>
      {/* 标题区域 */}
      <div className="flex flex-col items-center space-y-4 lg:space-y-6">
        {/* 主标题: 28px, Figtree Black */}
        <h1
          className={`text-center text-[28px] font-black leading-normal text-text-primary lg:text-[34px] xl:text-[48px] [&>span]:text-brand-red-100 ${isZh ? 'font-sourceHanSans' : ''}`}
          dangerouslySetInnerHTML={{ __html: t('hero-标题') }}
        />

        {/* 副标题: mobile 16px / lg+ 20px, Figtree Regular */}
        <p className="text-center text-[16px] font-normal leading-[1.2] text-text-primary lg:text-[20px]">
          {t('hero-副标题')}
        </p>

        {/* Trustpilot 评分区域 */}
        <TPLink>
          {/* mobile: gap-8px, lg: gap-16px, xl: gap-24px */}
          <div className="flex items-center space-x-2 lg:space-x-4 xl:space-x-6">
            <span className="h-[14px] w-[81px] lg:h-5 lg:w-[116px] xl:h-7 xl:w-[172px]">
              <Image
                alt="Trustpilot rating"
                className="h-full w-auto object-contain"
                width={172}
                height={28}
                src="/images/wukong-reviews/star.png"
                quality={100}
              />
            </span>

            {/* 评分信息: mobile 14px / lg 16px / xl 20px, Figtree */}
            <div className="flex items-center space-x-1 text-[14px] lg:space-x-2 lg:text-[16px] xl:text-[20px]">
              <span className="font-normal text-text-primary">
                Trustpilot {rating}
              </span>
              <span className="text-[12px] text-text-secondary_2">|</span>
              <span className="text-text-primary">
                <span className="font-semibold lg:font-bold xl:font-bold">
                  {t('hero-评分数')}
                </span>{' '}
                <span className="font-normal">{t('hero-评分文案')}</span>
              </span>
            </div>
          </div>
        </TPLink>
      </div>

      {/* 奖项卡片区域 - PC端 */}
      <div className="hidden w-full flex-col items-center space-y-8 lg:flex">
        {/* 第一行奖项 */}
        <div className="-mb-3 -mr-3 flex w-full flex-nowrap items-end justify-center xl:-mb-4 xl:-mr-4">
          {AWARDS_ROW_1.map(award => (
            <div key={award.id} className="mb-3 mr-3 xl:mb-4 xl:mr-4">
              <AwardCard
                name={award.name}
                year={award.year}
                highlighted={award.highlighted}
                logo={award.logo}
                widthClass={award.widthClass}
              />
            </div>
          ))}
        </div>

        {/* 第二行奖项 */}
        <div className="-mr-3 flex flex-nowrap items-start justify-center xl:-mr-4">
          {AWARDS_ROW_2.map(award => (
            <div key={award.id} className="mr-3 xl:mr-4">
              <AwardCard
                name={award.name}
                year={award.year}
                subtitle={award.subtitle}
                logo={award.logo}
                widthClass={award.widthClass}
                row={2}
              />
            </div>
          ))}
        </div>
      </div>

      {/* 奖项卡片区域 - 移动端：上方固定大卡片 + 下方 Swiper 滚动小卡片 */}
      <div className="flex w-full flex-col space-y-3 lg:hidden">
        {/* 上方固定的大卡片 - Cognia */}
        <MobileMainCard
          name="Cognia School Of Distinction"
          year="2024"
          highlighted
          logo="/images/wukong-reviews/logo-4.png"
        />

        {/* 下方 Swiper 滚动的小卡片 - 右侧露出部分卡片，末尾与右边框留间隔 */}
        <div className="-mr-4 overflow-visible pr-4">
          <Swiper
            modules={[Autoplay]}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            slidesPerView="auto"
            spaceBetween={12}
            className="!overflow-visible">
            {/* 除了 Cognia 之外的所有奖项 */}
            {[
              ...AWARDS_ROW_1.filter(a => a.id !== 'cognia'),
              ...AWARDS_ROW_2,
            ].map(award => (
              <SwiperSlide key={award.id} className="!w-auto">
                <MobileScrollCard
                  name={award.name}
                  year={award.year}
                  subtitle={'subtitle' in award ? award.subtitle : undefined}
                  logo={award.logo}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* CTA 按钮 */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={handleBookClassTrace}>
        <LeadButton
          asCustomButton
          callbackUrl={
            typeof window !== 'undefined' ? window.location.href : ''
          }
          traceName="O_WallOfLove_HeroSection_CTAClick">
          <Button
            size="large"
            className="mb-6 h-[52px] px-[30px] text-[20px] font-bold lg:mb-8 lg:h-[55px] lg:px-12 lg:text-[18px] xl:mb-15 xl:h-[77px] xl:px-[64px] xl:text-[24px]">
            {t('hero-按钮')}
          </Button>
        </LeadButton>
      </div>
    </section>
  );
};

export default HeroSection;
